import React from 'react';
import { Box, Button, Chip, CircularProgress, Typography } from '../ui';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';

interface ExternalCourseSectionProps {
  eventDate?: string | null;
  isExternalCompleted: boolean;
  isEnrolled: boolean;
  completedAt?: string | null;
  learnerVerificationActive: boolean;
  externalUrl?: string | null;
  externalLoading: boolean;
  onEnroll: () => void;
  onOpenExternalUrl: () => void;
  onOpenExternalEvent: () => void;
  verificationPanel?: React.ReactNode;
}

const ExternalCourseSection: React.FC<ExternalCourseSectionProps> = ({
  eventDate,
  isExternalCompleted,
  isEnrolled,
  completedAt,
  learnerVerificationActive,
  externalUrl,
  externalLoading,
  onEnroll,
  onOpenExternalUrl,
  onOpenExternalEvent,
  verificationPanel,
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 6,
        borderRadius: '12px',
        border: 'solid 1px rgb(228, 228, 228)',
        bgcolor: 'grey.50',
      }}
    >
      {/* External Event Badge */}
      <Chip icon={<OpenInNewIcon sx={{ fontSize: 16 }} />} label="External Event" color="secondary" sx={{ mb: 3, fontWeight: 600 }} />

      {/* Event Date */}
      {eventDate && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <EventIcon color="primary" />
          <Typography variant="h6" color="primary" fontWeight="bold">
            {new Date(eventDate).toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      )}

      {/* Status Indicator */}
      {isExternalCompleted ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="body1" color="success.main" fontWeight="bold">
            Completed
          </Typography>
          {completedAt && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              on {new Date(completedAt).toLocaleDateString()}
            </Typography>
          )}
        </Box>
      ) : isEnrolled ? (
        <Chip label="Enrolled" size="small" color="info" sx={{ mb: 3, fontWeight: 600 }} />
      ) : null}

      {/* Description — wording depends on which completion
          path is active. Verification configured means the
          learner has a two-step flow (open URL, then verify
          on this page); legacy fallback marks complete on
          open. */}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center', maxWidth: 500 }}>
        {isExternalCompleted
          ? 'You have completed this external event. Click below to open the event again.'
          : learnerVerificationActive
            ? 'Join the event using the button below. After it ends, come back here to mark your attendance using the verification panel.'
            : 'Click the button below to open the external event. This will mark your attendance as complete.'}
      </Typography>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', alignItems: 'center' }}>
        {!isEnrolled && !isExternalCompleted && (
          <Button variant="outlined" color="primary" onClick={onEnroll} sx={{ minWidth: 200 }}>
            Enroll
          </Button>
        )}
        <Button
          variant="contained"
          color={isExternalCompleted ? 'inherit' : 'primary'}
          // Open-only when verification is configured (and the
          // learner hasn't completed yet); legacy open+complete
          // when no verification is set up. Once completed,
          // either path just opens the URL again.
          onClick={
            isExternalCompleted || learnerVerificationActive
              ? onOpenExternalUrl
              : onOpenExternalEvent
          }
          disabled={externalLoading || !externalUrl}
          startIcon={externalLoading ? <CircularProgress size={20} color="inherit" /> : <OpenInNewIcon />}
          sx={{ minWidth: 200 }}
        >
          {externalLoading
            ? 'Opening...'
            : isExternalCompleted
              ? 'Open Again'
              : learnerVerificationActive
                ? 'Join Event'
                : 'Open Event'}
        </Button>
      </Box>

      {/* External URL Preview */}
      {externalUrl && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, wordBreak: 'break-all' }}>
          {externalUrl}
        </Typography>
      )}

      {/* ── Attendance verification panel ──────────────────
          Renders only when the admin enabled at least one
          learner-side method AND the learner hasn't already
          completed. Each enabled method gets its own
          sub-block; OR-logic (verify handler accepts any
          one satisfied). */}
      {verificationPanel}
    </Box>
  );
};

export default ExternalCourseSection;
